import classNames from 'classnames';
import { IServices, Logo, MenuAction, MenuCustomAction } from 'lego-on-react';
import { times } from 'lodash';
import * as React from 'react';

import { DevelopmentToolsContainer } from '../../containers/DevelopmentToolsContainer/DevelopmentToolsContainer';
import { HeaderInfoBlockContainer } from '../../containers/HeaderInfoBlockContainer/HeaderInfoBlockContainer';
import { HeaderUserInfoContainer } from '../../containers/HeaderUserInfoContainer/HeaderUserInfoContainer';
import { Bem } from '../../service/Bem/Bem';
import { HeaderHelpers } from '../../service/HeaderHelpers/HeaderHelpers';
import { LangUtils } from '../../service/Lang/Lang';
import { TestBem } from '../../service/TestBem';
import { Lang, NavLinkData } from '../../typings/common';
import { DevInfo } from '../../typings/dev';
import { CurrencyCode } from '../../typings/graphql-grid';
import { BalanceCart } from '../BalanceCart/BalanceCart';
import { DirectLogo } from '../DirectLogo/DirectLogo';
import { FeedbackTooltip } from '../FeedbackTooltip/FeedbackTooltip';
import { HeaderInfoMenu } from '../HeaderInfoMenu/HeaderInfoMenu';
import { ServicesMenu } from '../SevicesMenu/ServicesMenu';
import { Skeleton } from '../Skeleton/Skeleton';
import { TabMenu, TabMenuSize, TabMenuTheme } from '../TabMenu/TabMenu';
import { WalletButton } from '../WalletButton/WalletButton';

import './Header.css';

export interface HeaderProps {
    enableSidebar: boolean;
    isBeta: boolean;
    currentLang: Lang;
    hasServicesMenu: boolean;
    fetchUserAccountsInfo: () => void;
    infoLinks: Array<NavLinkData>;
    tabLinks: Array<NavLinkData>;
    sendGridFeedback: (message: string) => void;
    isFeedbackPromoNeedToShow: boolean;
    initFeedbackPromo: () => void;
    switchToOldInterface: () => void;
    wallet: {
        id?: string;
        url: string;
        sum?: number;
        currency?: CurrencyCode;
    };
    devInfo: DevInfo;
    isFeedbackVisible: boolean;
    isSwitchToOldInterfaceLinkVisible: boolean;
    fetchHeaderInfoData: () => void;
    isInfoBlockAvailable: boolean;
    commonWalletClick: (e: React.MouseEvent<HTMLElement>) => void;
    needWallet: boolean;
    actionsMenu: (MenuAction | MenuCustomAction)[];
    additionalUserInfo: string;
    onRecommendationTooltipClose: () => void;
    isB2bBalanceCartEnabled: boolean;
    balanceCartHost?: string;
}

const HEADER_TABS_SKELETON_COUNT = 5;
const BLOCK = 'header';

export class Header extends React.Component<HeaderProps> {
    constructor(props: HeaderProps) {
        super(props);

        if (props.isFeedbackPromoNeedToShow) {
            HeaderHelpers.resetNewInterfacePromoShowTime();
        } else {
            props.initFeedbackPromo();
        }
    }

    componentDidMount() {
        this.props.fetchUserAccountsInfo();
    }

    render() {
        const {
            isFeedbackVisible,
            currentLang,
            hasServicesMenu,
            infoLinks,
            wallet,
            switchToOldInterface,
            isInfoBlockAvailable,
            commonWalletClick,
            needWallet,
            enableSidebar,
            isSwitchToOldInterfaceLinkVisible
        } = this.props;

        return (
            <div className={classNames(Bem.cls(BLOCK))}>
                {hasServicesMenu ? (
                    <ServicesMenu
                        lang={currentLang}
                        cls={Bem.cls(BLOCK, 'service-menu')}
                    />
                ) : null}

                <div className={Bem.cls(BLOCK, 'logos')}>
                    <Logo
                        type={'link'}
                        // tslint:disable-next-line
                        url={IServices.serviceUrl('www')}
                        tabIndex={-1}
                        name={
                            [Lang.en, Lang.tr].includes(currentLang)
                                ? 'ys-en-69x28'
                                : 'ys-ru-69x28'
                        }
                        cls={Bem.cls(BLOCK, 'ya-logo')}
                    />
                    <a
                        href="/"
                        className={Bem.cls(BLOCK, 'direct-logo', {
                            lang: LangUtils.getCurrentLang()
                        })}
                        tabIndex={-1}
                    >
                        <DirectLogo currentLang={currentLang} />
                    </a>
                </div>

                {this.getTabMenu()}

                {needWallet && (
                    <WalletButton {...wallet} onClick={commonWalletClick} />
                )}

                <div
                    className={classNames(
                        Bem.cls(BLOCK, 'icons'),
                        TestBem.cls(BLOCK, 'icons')
                    )}
                >
                    {this.renderBalanceCart()}

                    {isFeedbackVisible && (
                        <FeedbackTooltip
                            cls={Bem.cls(BLOCK, 'feedback')}
                            showPromo={this.props.isFeedbackPromoNeedToShow}
                            onSaveClick={this.props.sendGridFeedback}
                            isSwitchToOldInterfaceLinkVisible={
                                isSwitchToOldInterfaceLinkVisible
                            }
                            onSwitchToOldInterfaceLinkClick={
                                switchToOldInterface
                            }
                        />
                    )}
                    {isInfoBlockAvailable && (
                        <HeaderInfoBlockContainer
                            cls={Bem.cls(BLOCK, 'info-block')}
                        />
                    )}
                    {!enableSidebar && (
                        <HeaderInfoMenu
                            cls={Bem.cls(BLOCK, 'info-menu')}
                            links={infoLinks}
                        />
                    )}
                </div>

                {this.getHeaderUserInfo()}
            </div>
        );
    }

    private renderBalanceCart = () => {
        const { isB2bBalanceCartEnabled, wallet, balanceCartHost } = this.props;

        // Для отображения корзины должны быть:
        // включена фича "b2b_balance_cart"
        // включен "Общий счет"
        // известен хост корзины
        if (!isB2bBalanceCartEnabled || !wallet.id || !balanceCartHost) {
            return null;
        }

        return <BalanceCart host={balanceCartHost} />;
    };

    private getTabMenu = () => {
        if (this.props.tabLinks.length === 0) {
            return this.getTabSkeletons();
        }

        const items = this.props.tabLinks.map(item => {
            if (item.title === undefined && item.getTitle !== undefined) {
                return {
                    ...item,
                    title: item.getTitle(
                        this.props.onRecommendationTooltipClose
                    )
                };
            }

            return item;
        });

        return (
            <TabMenu
                className={Bem.cls(BLOCK, 'tab-menu')}
                items={items}
                size={TabMenuSize.m}
                theme={TabMenuTheme.grey}
            />
        );
    };

    private getTabSkeletons = () => {
        return times(HEADER_TABS_SKELETON_COUNT, (key: number) => (
            <Skeleton
                key={key}
                isAnimating={true}
                height={18}
                cls={Bem.cls(BLOCK, 'tab-menu-skeleton')}
            />
        ));
    };

    private getHeaderUserInfo = () => {
        const { devInfo } = this.props;

        return (
            <div
                className={classNames(
                    Bem.cls(BLOCK, 'user-block'),
                    TestBem.cls(BLOCK, 'user-block')
                )}
            >
                <HeaderUserInfoContainer cls={Bem.cls(BLOCK, 'user-info')} />
                {devInfo.isBeta && <DevelopmentToolsContainer />}
            </div>
        );
    };
}
